// OSRM + Nominatim public services (no API key required)

const OSRM_URL = "https://router.project-osrm.org";
const NOMINATIM_URL = "https://nominatim.openstreetmap.org";

/**
 * Get a driving route between two points.
 * Returns array of [lat, lng] waypoints plus distance (m) and duration (s).
 */
export async function getRoute(from, to) {
  try {
    const url = `${OSRM_URL}/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.routes || data.routes.length === 0) return null;
    const route = data.routes[0];
    const coords = route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
    return {
      coordinates: coords,
      distance: route.distance,
      duration: route.duration,
    };
  } catch {
    return null;
  }
}

/**
 * Search places by text query.
 * `viewbox` is optional: [lng_min, lat_max, lng_max, lat_min] to bias results.
 */
export async function searchPlaces(query, viewbox = null, limit = 6) {
  if (!query || query.length < 2) return [];
  try {
    const params = new URLSearchParams({
      q: query,
      format: "json",
      limit: String(limit),
      addressdetails: "1",
    });
    if (viewbox) {
      params.append("viewbox", viewbox.join(","));
      params.append("bounded", "1");
    }
    const url = `${NOMINATIM_URL}/search?${params.toString()}`;
    const res = await fetch(url, {
      headers: { "Accept-Language": "fr" },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.map((p) => ({
      label: p.display_name,
      short: p.name || p.display_name.split(",")[0],
      lat: parseFloat(p.lat),
      lng: parseFloat(p.lon),
      type: p.type,
    }));
  } catch {
    return [];
  }
}

/**
 * Reverse geocode lat/lng to address.
 */
export async function reverseGeocode(lat, lng) {
  try {
    const url = `${NOMINATIM_URL}/reverse?format=json&addressdetails=1&extratags=1&namedetails=1&zoom=18&lat=${lat}&lon=${lng}`;
    const res = await fetch(url, { headers: { "Accept-Language": "fr" } });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      label: data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
      short:
        data.name ||
        data.address?.road ||
        data.address?.neighbourhood ||
        data.address?.suburb ||
        data.display_name?.split(",")[0] ||
        "Position selectionnee",
      lat,
      lng,
      type: data.type,
      category: data.category,
      osm_type: data.osm_type,
      osm_id: data.osm_id,
      address: data.address || {},
      extratags: data.extratags || {},
      namedetails: data.namedetails || {},
    };
  } catch {
    return null;
  }
}

/**
 * Build a viewbox around a center (useful to bias search to current city).
 * radius in km roughly.
 */
export function viewboxAround(center, radiusKm = 30) {
  const dLat = radiusKm / 111;
  const dLng = radiusKm / (111 * Math.cos((center.lat * Math.PI) / 180));
  return [
    center.lng - dLng, // lng_min
    center.lat + dLat, // lat_max
    center.lng + dLng, // lng_max
    center.lat - dLat, // lat_min
  ];
}
